import { useState, useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import ReservationModal from './modals/ReservationModal';
import SelectedSlotDialog from './modals/SelectedSlotDialog';
import SlotSelectionDialog from './modals/SlotSelectionDialog';
import { createStaffTimeSlots, createReservation, updateReservation, approveReservation, deleteReservation, cancelReservation } from '@services/reservationService';
import { formatTime, formatDisplayTime, calendarFormatDate, getRelativeDay } from './utils/calendarHelpers';
import './ReservationCalendar.scss';
import { useCalendarState } from './hooks/useCalendarState'; // Import the custom hook
import { changeSlotMode } from '@services/reservationService';
import ConfirmDialog from '@components/common/ConfirmDialog'; // Adjust the import path as necessary
import { useTranslation } from 'react-i18next'; // Add this import
import { getSlotClass } from '@utils/slotUtils';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker
import dayjs from 'dayjs'; // Import dayjs for date manipulation
import { differenceInCalendarDays } from 'date-fns';
import i18n from '../../i18n';
import { format } from 'date-fns'; // Import format from date-fns
import { getTimeFormat } from '@utils/dateHelper';
import type { Reservation as BaseReservation, TimeSlot as BaseTimeSlot, CancelReservationParams, CreateReservationParams, UpdateReservationParams, ReservationStaff } from '@app-types/models/reservation';
import type { StylistAvailability, BusinessHours } from '@app-types/models/user';
import type { ReservationModalProps } from './modals/ReservationModal';
import type { SelectedSlotDialogProps } from './modals/SelectedSlotDialog';
import type { SlotSelectionDialogProps } from './modals/SlotSelectionDialog';

interface TimeSlot {
  _id: string;
  start: string;
  end: string;
  title: string;
  remark: string;
  isOpen: boolean;
}

interface Stylist {
  _id: string;
  availability: StylistAvailability;
  [key: string]: unknown;
}

interface ReservationPlan {
  _id: string;
  name: string;
  zh_name?: string;
  [key: string]: unknown;
}

interface ReservationUser {
  _id: string;
  email?: string;
  displayName?: string;
  phone?: string;
  [key: string]: unknown;
}

interface Reservation {
  _id: string;
  customerName?: string;
  start: string;
  end: string;
  status: string;
  planId?: ReservationPlan | string;
  userId?: ReservationUser;
  staffId: string | ReservationStaff;
  createdAt?: string;
  remark?: string;
  createdType?: string;
  paymentType?: string;
  couponId?: { _id: string; value?: number; [key: string]: unknown } | string;
  originalAmount?: number | string;
  discountedAmount?: number | string;
  deductCredit?: number | string;
  customerPhone?: string;
  [key: string]: unknown;
}

interface Plan {
  _id: string;
  name: string;
  price?: number;
}

interface Business {
  businessHours: BusinessHours;
  [key: string]: unknown;
}

interface SlotDetail {
  isOpen: boolean;
  selectedOption: string;
  title: string;
  remark: string;
  reservationData: {
    originalAmount?: number;
    discountedAmount?: number;
    [key: string]: unknown;
  };
  start: Date;
  end: Date;
}

interface CancelData {
  cancelledReason: string;
  cancelledRemark: string;
}

interface ReservationCalendarProps {
  stylist: Stylist;
  reservations: Reservation[];
  plans: Plan[];
  business: Business;
  refetchData: () => void;
}

const ReservationCalendar = ({ stylist, reservations, plans, business, refetchData }: ReservationCalendarProps) => {
  const { t } = useTranslation();
  const calendarGridRef = useRef<HTMLDivElement>(null);
  const [rearrangeMode, setRearrangeMode] = useState(false);
  const [rearrangingReservation, setRearrangingReservation] = useState<Reservation | null>(null);
  const [stylistData, setStylistData] = useState<Stylist>(stylist);
  const [reservationData, setReservationData] = useState<Reservation[]>([]);
  const [selectedSlotBlock, setSelectedSlotBlock] = useState<TimeSlot | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    startDayOffset,
    setStartDayOffset,
    calendar,
    resetCalendarByStylistData,
    resetCalendar,
    getReservation,
    getSlotBlock,
    handleMouseDown: hookHandleMouseDown,
    handleMouseEnter: hookHandleMouseEnter,
    selectedSlots,
    showCreateModal,
    setShowCreateModal,
    resetSelectedSlots,
    getDayOff,
    setConfirmRearrange,
    confirmRearrange,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useCalendarState(stylistData, business);

  const handleSlotInteraction = ({ dayIndex, slotIndex, reservation, slotBlock, isDayOff }: {
    dayIndex: number;
    slotIndex: number;
    reservation: Reservation | undefined;
    slotBlock: BaseTimeSlot | null;
    isDayOff: boolean;
  }) => {
    const isRearrangingCurrentReservation = rearrangeMode && rearrangingReservation && reservation && reservation._id === rearrangingReservation._id;

    const result = hookHandleMouseDown({
      dayIndex,
      slotIndex,
      reservation: isRearrangingCurrentReservation ? null : (reservation as BaseReservation | undefined),
      slotBlock,
      rearrangeMode,
      rearrangingReservation: rearrangingReservation as BaseReservation | undefined,
      reservationData: reservationData as BaseReservation[],
      isDayOff
    });

    if (!result) return;

    if ('error' in result) {
      alert(result.error);
      return;
    }

    if (result.type === 'reservation') {
      setSelectedReservation(result.reservation as Reservation);
    } else if (result.type === 'slotBlock') {
      setSelectedSlotBlock(result.slotBlock as TimeSlot);
    }
  };

  useEffect(() => {
    setStylistData(stylist);
  }, [stylist]);

  useEffect(() => {
    setReservationData(reservations);
  }, [reservations]);

  const handlePrevious = () => {
    setStartDayOffset((prev: number) => prev - 3);
    resetCalendar();
  };

  const handleNext = () => {
    setStartDayOffset((prev: number) => prev + 3);
    resetCalendar();
  };

  const handleSave = (slotDetail: SlotDetail) => {
    if (slotDetail.selectedOption === 'open' || slotDetail.selectedOption === 'block') {
      createStaffTimeSlots({
        staffId: stylistData._id,
        timeSlots: [{
          start: slotDetail.start.toISOString(),
          end: slotDetail.end.toISOString(),
          isOpen: slotDetail.isOpen
        }]
      }).then(() => {
        refetchData();
        resetCalendarByStylistData();
      }).catch((error: unknown) => {
        console.error('Error creating staff time slots:', error);
      }).finally(() => {
        resetSelectedSlots();
      });
    } else if (slotDetail.selectedOption === 'reserve') {
      const reservationPayload = {
        ...slotDetail.reservationData,
        staffId: stylist._id,
        start: slotDetail.start.toISOString(),
        end: slotDetail.end.toISOString(),
        originalAmount: slotDetail.reservationData?.originalAmount,
        discountedAmount: slotDetail.reservationData?.discountedAmount
      };
      createReservation(reservationPayload as CreateReservationParams).then(() => {
        refetchData();
      }).catch((error: unknown) => {
        console.error('Error creating reservation:', error);
      }).finally(() => {
        resetSelectedSlots();
      });
    }
  };

  const handleUpdate = (reservation: Reservation) => {
    updateReservation(reservation._id, reservation as UpdateReservationParams).then(() => {
      refetchData();
    }).catch((error: unknown) => {
      console.error('Error updating reservation:', error);
    });
  };

  const handleApprove = (reservation: Reservation) => {
    approveReservation(reservation._id).then(() => {
      refetchData();
    }).catch((error: unknown) => {
      console.error('Error approving reservation:', error);
    });
  };

  const handleDelete = (reservation: Reservation) => {
    deleteReservation(reservation._id).then(() => {
      refetchData();
    }).catch((error: unknown) => {
      console.error('Error deleting reservation:', error);
    });
  };

  const handleCancel = (reservation: Reservation, cancelData: CancelData) => {
    console.log('cancelData', cancelData, reservation._id);
    cancelReservation(reservation._id, cancelData as CancelReservationParams).then(() => {
      refetchData();
    }).catch((error: unknown) => {
      console.error('Error cancelling reservation:', error);
    });
  };

  useEffect(() => {
    setStartDayOffset(0);
  }, [setStartDayOffset]);

  const handleConfirmChangeSlotMode = () => {
    changeSlotMode(stylistData._id, { mode: stylistData.availability.isDefaultClose ? 'open' : 'close' }).then(() => {
      refetchData();
    }).catch((error: unknown) => {
      console.error('Error changing slot mode:', error);
    }).finally(() => {
      setDialogOpen(false);
    });
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    const today = new Date();
    const selectedDate = new Date(date.toISOString());
    const offset = differenceInCalendarDays(selectedDate, today);
    setStartDayOffset(offset);
    resetCalendar();
  };

  return (
    <Stack spacing={2} sx={{ marginRight: {xs: 5, sm: 0}, marginTop: 2, width: {xs: 'calc(100vw - 70px)', sm: 'auto'} }}>
      <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
        <FormLabel component="legend">{t('reservationCalendar.slotModeTitle')}</FormLabel>
        <RadioGroup
          row
          aria-label="slot-mode"
          name="slot-mode-radio-buttons-group"
          value={stylistData.availability.isDefaultClose ? 'close' : 'open'}
          onChange={() => setDialogOpen(true)}
        >
          <FormControlLabel
            value="open"
            control={<Radio />}
            label={t('reservationCalendar.defaultOpenAllSlots')}
          />
          <FormControlLabel
            value="close"
            control={<Radio />}
            label={t('reservationCalendar.defaultCloseAllSlots')}
          />
        </RadioGroup>
      </FormControl>

      {rearrangeMode && (
        <Button
          sx={{
            position: 'fixed',
            right: '20px',
            top: '10%',
            transform: 'translateY(-50%)',
            zIndex: 1000
          }}
          variant="contained"
          color="secondary"
          onClick={() => {
            setRearrangeMode(false);
            setRearrangingReservation(null);
          }}
        >
          {t('reservationCalendar.cancelRearrange')}
        </Button>
      )}

      <ConfirmDialog
        open={dialogOpen}
        title={t('reservationCalendar.confirmSlotModeChange')}
        message={t('reservationCalendar.confirmSlotModeChangeMessage', {
          action: stylistData.availability.isDefaultClose ? t('reservationCalendar.open') : t('reservationCalendar.close')
        })}
        onConfirm={handleConfirmChangeSlotMode}
        onCancel={() => setDialogOpen(false)}
      />

      <DatePicker
        label={t('reservationCalendar.selectDate')}
        value={dayjs().add(startDayOffset, 'day')}
        onChange={handleDateChange}
        format="DD/MM/YYYY"
        minDate={dayjs().subtract(1, 'month')}
        maxDate={dayjs().add(6, 'month')}
      />

      <Box className="navigation-buttons">
        {startDayOffset > -3 ? (
          <Button variant="contained" color="secondary" onClick={handlePrevious}>
            {t('reservationCalendar.previous')}
          </Button>
        ) : (
          <Box></Box>
        )}
        <Button variant="contained" color="secondary" onClick={handleNext}>
          {t('reservationCalendar.next')}
        </Button>
      </Box>
      <Box className="calendar-grid" ref={calendarGridRef} tabIndex={-1}>
        {calendar.map((day: boolean[], dayIndex: number) => (
          <div key={dayIndex} className="day-column">
            <Box className="day-header" sx={{ backgroundColor: 'primary.main', color: 'white' }}>
              <Typography variant="caption">
                {getRelativeDay(dayIndex, startDayOffset)}
              </Typography>
              <br/>
              <Typography variant="caption">
                {calendarFormatDate(dayIndex, startDayOffset)}
              </Typography>
            </Box>
            {day.map((isOpen: boolean, slotIndex: number) => {
              const reservation = getReservation(dayIndex, slotIndex, reservationData as BaseReservation[]) as Reservation | undefined;
              const slotBlock = getSlotBlock(dayIndex, slotIndex);
              const isDayOff = getDayOff(dayIndex, startDayOffset);
              const slotClass = getSlotClass(reservation as BaseReservation | undefined, slotBlock, isOpen, rearrangeMode);
              const slotTime = formatTime(slotIndex, stylistData.availability?.startHour || business.businessHours.startHour);
              const slotTimePlusOne = formatTime(slotIndex + 1, stylistData.availability?.startHour || business.businessHours.startHour);
              const isRearrangingCurrentReservation = rearrangeMode && rearrangingReservation && reservation && reservation._id === rearrangingReservation._id;

              // Determine if this slot is the start or end of a reservation
              const isStartSlot = reservation && slotTime === getTimeFormat(reservation.start);
              const isEndSlot = reservation && slotTimePlusOne === getTimeFormat(reservation.end);

              return (
                <div
                  key={slotIndex}
                  className={`time-slot ${isDayOff ? 'day-off' : slotClass} ${isStartSlot ? 'reservation-start' : ''} ${isEndSlot ? 'reservation-end' : ''}`}
                  onMouseDown={() => handleSlotInteraction({ dayIndex, slotIndex, reservation, slotBlock, isDayOff })}
                  onMouseEnter={() => hookHandleMouseEnter({ dayIndex, slotIndex, isReserved: !!reservation, rearrangeMode })}
                  onTouchStart={(e) => handleTouchStart(e.nativeEvent, { dayIndex, slotIndex, reservation: reservation as BaseReservation | undefined, slotBlock, isDayOff, rearrangeMode, rearrangingReservation: rearrangingReservation as BaseReservation | undefined, reservationData: reservationData as BaseReservation[] })}
                  onTouchMove={(e) => handleTouchMove(e.nativeEvent)}
                  onTouchEnd={handleTouchEnd}
                  data-slot-index={slotIndex}
                  data-day-index={dayIndex}
                >
                  <span className="time-display">
                    {formatDisplayTime(formatTime(slotIndex, stylistData.availability?.startHour || business.businessHours.startHour))}
                  </span>
                  {reservation && !isRearrangingCurrentReservation ? (
                    <div className="marquee-container">
                      <div className="marquee">
                        {
                          reservation.status === 'pending-for-approval' ?
                          (<div>{t('reservationCalendar.pendingForApproval')}</div>) :
                          (<span className="marquee-content">
                            {
                              `- ${reservation.customerName || ''}${reservation?.planId ? ` - ${(reservation.planId as ReservationPlan)[i18n.language === 'zh' ? 'zh_name' : 'name']}` : ''}`
                            }
                          </span>)
                        }
                      </div>
                    </div>
                  ) : (
                    <div className="slot-status-container">
                      <div className="slot-status">{isDayOff ? t('reservationCalendar.dayOff') : slotClass === 'blocked' && slotBlock && `- ${t('reservationCalendar.blocked')} - ${slotBlock.title}`}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </Box>
      {selectedReservation && (
        <ReservationModal
          plans={plans}
          onSubmit={(data) => handleUpdate({ ...selectedReservation, ...data } as Reservation)}
          reservation={selectedReservation as ReservationModalProps['reservation']}
          onClose={() => setSelectedReservation(null)}
          onRearrange={(reservation) => {
            setRearrangeMode(true);
            setRearrangingReservation(reservation as Reservation);
            setSelectedReservation(null); // Close modal after setting rearrange mode
            setTimeout(() => calendarGridRef.current?.focus({ preventScroll: true }), 0);
          }}
          onApprove={(reservation) => handleApprove(reservation as Reservation)}
          onDelete={(reservation) => handleDelete(reservation as Reservation)}
          onCancel={(cancelData: CancelData) => handleCancel(selectedReservation, cancelData)}
        />
      )}
      {selectedSlotBlock && (
        <SelectedSlotDialog
          isOpen={selectedSlotBlock.isOpen}
          stylist={stylistData as SelectedSlotDialogProps['stylist']}
          setStylistData={(data) => setStylistData(data as Stylist)}
          slot={selectedSlotBlock as SelectedSlotDialogProps['slot']}
          onClose={() => setSelectedSlotBlock(null)}
        />
      )}
      {showCreateModal && (
        <SlotSelectionDialog
          stylist={stylistData as SlotSelectionDialogProps['stylist']}
          plans={plans}
          selectedSlots={selectedSlots}
          onSave={(detail) => handleSave(detail as SlotDetail)}
          onClose={() => {
            setShowCreateModal(false);
            resetCalendarByStylistData();
            resetSelectedSlots();
          }}
        />
      )}
      {confirmRearrange && (
        <ConfirmDialog
          open={!!confirmRearrange}
          title={t('reservationCalendar.confirmRearrangeTitle')}
          message={t('reservationCalendar.confirmRearrangeMessage', {
            date: format(confirmRearrange.newStart, 'yyyy-MM-dd'),
            start: format(confirmRearrange.newStart, 'HH:mm'),
            end: format(confirmRearrange.newEnd, 'HH:mm')
          })}
          onConfirm={() => {
            updateReservation(rearrangingReservation!._id, {
              start: confirmRearrange.newStart.toISOString(),
              end: confirmRearrange.newEnd.toISOString()
            }).then(refetchData)
              .catch((error: unknown) => console.error('Error updating reservation:', error))
              .finally(() => {
                setRearrangeMode(false);
                setRearrangingReservation(null);
                setConfirmRearrange(null);
              });
          }}
          onCancel={() => setConfirmRearrange(null)}
        />
      )}
    </Stack>
  );
};

export default ReservationCalendar;
